import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../Modal/Modal';
import { api, VEHICLE_DOTACION_CATEGORIES, isVehicleDotacionMaterial } from '../../services/api';

function formatDateTime(raw) {
  const s = String(raw || '').trim();
  if (!s) return '-';
  try {
    return new Date(s.replace(' ', 'T')).toLocaleString('es-CO', { timeZone: 'America/Bogota' });
  } catch (_) {
    return s;
  }
}

// Agrupa las filas crudas de un cargue (movimientos tipo entrada con el
// mismo entry_ref) por material + marca, igual criterio que EntryModule
// usa para armar la plantilla de Entrada masiva: seriados por separado
// (uno por fila) y sin serial sumando cantidades.
function groupBatchRows(rows) {
  const byKey = new Map();
  (rows || []).forEach((r) => {
    const materialId = Number(r.material_id) || 0;
    if (!materialId) return;
    const brand = String(r.brand || '').trim();
    const serial = String(r.serial || '').trim();
    const key = `${materialId}||${brand.toLowerCase()}||${serial ? 'serial' : 'qty'}`;
    if (!byKey.has(key)) {
      byKey.set(key, {
        materialId,
        materialCode: r.material_code || '',
        materialName: r.material_name || '',
        materialUnit: r.material_unit || 'Unidad',
        brand,
        isSerial: !!serial,
        serials: [],
        quantity: 0,
      });
    }
    const g = byKey.get(key);
    if (serial) g.serials.push(serial);
    else g.quantity += Number(r.quantity) || 0;
  });
  return Array.from(byKey.values());
}

export function AssignFromEntryModal({ materials, people, warehouses = [], onClose, onConfirm }) {
  const [step, setStep] = useState('list');
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [q, setQ] = useState('');
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [qtyOverrides, setQtyOverrides] = useState({});
  const [availableSerialsByMaterial, setAvailableSerialsByMaterial] = useState({});
  const [assigneeType, setAssigneeType] = useState('person');
  const [responsibleDoc, setResponsibleDoc] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [observations, setObservations] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadBatches = async (query) => {
    setLoadingBatches(true);
    try {
      const res = await api.movements.entryBatches({ q: query || '', limit: 50 });
      setBatches(Array.isArray(res?.data) ? res.data : []);
    } catch (_) {
      setBatches([]);
    } finally {
      setLoadingBatches(false);
    }
  };

  useEffect(() => { loadBatches(''); }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.vehicles.get({ limit: 500 });
        const rows = Array.isArray(res?.data) ? res.data : [];
        // Los vehículos inactivos (fuera de la empresa) no se asignan.
        if (!cancelled) setVehicles(rows.filter((v) => String(v?.status || 'Activo').toLowerCase() !== 'inactivo'));
      } catch (_) {
        if (!cancelled) setVehicles([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const warehouseName = (id) => (warehouses.find((w) => w.id === id)?.name || id || '-');

  const openBatch = async (batch) => {
    setSelectedBatch(batch);
    setStep('detail');
    setLoadingDetail(true);
    setError('');
    try {
      const res = await api.movements.get({ type: 'entrada', entry_ref: batch.entry_ref, limit: 1000 });
      const raw = Array.isArray(res?.data) ? res.data : [];
      const grouped = groupBatchRows(raw);
      setRows(grouped);

      // Disponibilidad de seriales: una consulta por material único del cargue.
      const uniqueMaterialIds = Array.from(new Set(grouped.filter((g) => g.isSerial).map((g) => g.materialId)));
      const availMap = {};
      await Promise.all(uniqueMaterialIds.map(async (mid) => {
        try {
          const r = await api.serials.list({ material_id: mid, warehouse_id: batch.warehouse_id });
          const arr = Array.isArray(r?.data) ? r.data : [];
          availMap[mid] = new Set(arr.map((x) => String(x.serial || '').trim()).filter(Boolean));
        } catch (_) {
          availMap[mid] = new Set();
        }
      }));
      setAvailableSerialsByMaterial(availMap);

      // Selección por defecto: todo lo disponible.
      const initialSelected = new Set();
      const initialQty = {};
      grouped.forEach((g, idx) => {
        const key = String(idx);
        if (g.isSerial) {
          const availSet = availMap[g.materialId] || new Set();
          const anyAvailable = g.serials.some((s) => availSet.has(s));
          if (anyAvailable) initialSelected.add(key);
        } else {
          const mat = (materials || []).find((m) => m.id === g.materialId);
          const stock = Number((mat?.stockByWarehouse || {})[batch.warehouse_id] || 0);
          const available = Math.min(g.quantity, stock);
          initialQty[key] = available;
          if (available > 0) initialSelected.add(key);
        }
      });
      setSelectedKeys(initialSelected);
      setQtyOverrides(initialQty);
    } catch (e) {
      setError(e?.message || 'No fue posible cargar el detalle del cargue.');
      setRows([]);
    } finally {
      setLoadingDetail(false);
    }
  };

  const backToList = () => {
    setStep('list');
    setSelectedBatch(null);
    setRows([]);
    setSelectedKeys(new Set());
    setQtyOverrides({});
    setError('');
  };

  const rowInfo = useMemo(() => {
    const wh = selectedBatch?.warehouse_id || '';
    return rows.map((g, idx) => {
      const key = String(idx);
      if (g.isSerial) {
        const availSet = availableSerialsByMaterial[g.materialId] || new Set();
        const availableSerials = g.serials.filter((s) => availSet.has(s));
        return { key, group: g, availableCount: availableSerials.length, totalCount: g.serials.length, availableSerials };
      }
      const mat = (materials || []).find((m) => m.id === g.materialId);
      const stock = Number((mat?.stockByWarehouse || {})[wh] || 0);
      const available = Math.min(g.quantity, stock);
      return { key, group: g, availableCount: available, totalCount: g.quantity };
    });
  }, [rows, availableSerialsByMaterial, materials, selectedBatch]);

  const toggleRow = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const canSubmit = useMemo(() => {
    if (assigneeType === 'vehicle' && !vehicleId) return false;
    if (assigneeType === 'person' && !responsibleDoc) return false;
    if (submitting) return false;
    return rowInfo.some((r) => selectedKeys.has(r.key) && r.availableCount > 0);
  }, [assigneeType, responsibleDoc, vehicleId, submitting, rowInfo, selectedKeys]);

  const submit = () => {
    if (!canSubmit) return;
    const person = (people || []).find((p) => String(p.dni || p.id) === String(responsibleDoc));
    const vehicle = vehicles.find((v) => String(v.id) === String(vehicleId));
    const warehouseId = selectedBatch?.warehouse_id || '';
    const items = [];
    rowInfo.forEach((r) => {
      if (!selectedKeys.has(r.key) || r.availableCount <= 0) return;
      const g = r.group;
      const mat = (materials || []).find((m) => m.id === g.materialId);
      if (assigneeType === 'vehicle' && mat && !isVehicleDotacionMaterial(mat)) {
        return;
      }
      if (assigneeType === 'person' && mat && isVehicleDotacionMaterial(mat)) {
        return;
      }
      const base = {
        assigneeType,
        vehicleId: assigneeType === 'vehicle' ? Number(vehicleId) : null,
        vehiclePlate: vehicle?.plate || '',
        materialId: g.materialId,
        materialName: g.materialName,
        quantity: 1,
        observations,
        brandName: g.brand,
        evidencePhotos: [],
        warehouseId,
      };
      if (assigneeType === 'vehicle') {
        base.responsibleDoc = null;
        base.responsibleName = vehicle?.plate ? `Vehículo ${vehicle.plate}` : `Vehículo #${vehicleId}`;
      } else {
        base.responsibleDoc = person?.dni || responsibleDoc;
        base.responsibleName = person?.name || responsibleDoc;
      }
      if (g.isSerial) {
        r.availableSerials.forEach((serial) => {
          items.push({ ...base, quantity: 1, serial });
        });
      } else {
        const qty = Math.max(0, Math.min(Number(qtyOverrides[r.key]) || 0, r.availableCount));
        if (qty <= 0) return;
        items.push({ ...base, quantity: qty, serial: '' });
      }
    });
    if (!items.length) { setError('No hay ítems disponibles seleccionados para asignar.'); return; }
    setSubmitting(true);
    onConfirm(items);
  };

  return (
    <Modal isOpen onClose={onClose} title={step === 'list' ? 'Asignar cargue completo' : `Cargue ${selectedBatch?.entry_ref || ''}`} size="max-w-3xl">
      {step === 'list' ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') loadBatches(q); }}
              placeholder="Buscar por referencia del cargue"
              className="flex-1 border rounded px-3 py-2"
            />
            <button type="button" onClick={() => loadBatches(q)} className="px-3 py-2 rounded border bg-white hover:bg-gray-50">
              Buscar
            </button>
          </div>
          {loadingBatches ? (
            <div className="text-sm text-gray-600 py-6 text-center">Cargando cargues...</div>
          ) : batches.length === 0 ? (
            <div className="text-sm text-gray-600 py-6 text-center">No hay cargues de entrada registrados.</div>
          ) : (
            <div className="border rounded overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left">Referencia</th>
                    <th className="px-3 py-2 text-left">Fecha</th>
                    <th className="px-3 py-2 text-left">Bodega</th>
                    <th className="px-3 py-2 text-right">Materiales</th>
                    <th className="px-3 py-2 text-right">Cantidad total</th>
                    <th className="px-3 py-2 text-left"></th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.entry_ref} className="border-t">
                      <td className="px-3 py-2 font-mono text-xs">{b.entry_ref}</td>
                      <td className="px-3 py-2 text-xs">{formatDateTime(b.created_at)}</td>
                      <td className="px-3 py-2">{warehouseName(b.warehouse_id)}</td>
                      <td className="px-3 py-2 text-right">{b.material_count}</td>
                      <td className="px-3 py-2 text-right">{b.total_quantity}</td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => openBatch(b)}
                          className="px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 text-xs"
                        >
                          Elegir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <button type="button" onClick={backToList} className="text-sm text-blue-700 hover:underline">
            ← Elegir otro cargue
          </button>

          <div className="text-sm text-gray-600">
            Bodega: <span className="font-medium text-gray-800">{warehouseName(selectedBatch?.warehouse_id)}</span> — Fecha: {formatDateTime(selectedBatch?.created_at)}
          </div>

          {loadingDetail ? (
            <div className="text-sm text-gray-600 py-6 text-center">Cargando ítems del cargue...</div>
          ) : (
            <div className="border rounded overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left"></th>
                    <th className="px-3 py-2 text-left">Material</th>
                    <th className="px-3 py-2 text-left">Marca</th>
                    <th className="px-3 py-2 text-left">Detalle</th>
                    <th className="px-3 py-2 text-right">Disponible</th>
                  </tr>
                </thead>
                <tbody>
                  {rowInfo.map((r) => {
                    const disabled = r.availableCount <= 0;
                    return (
                      <tr key={r.key} className={`border-t ${disabled ? 'opacity-50' : ''}`}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedKeys.has(r.key)}
                            disabled={disabled}
                            onChange={() => toggleRow(r.key)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-800">{r.group.materialName}</div>
                          <div className="text-xs text-gray-500">{r.group.materialCode}</div>
                        </td>
                        <td className="px-3 py-2">{r.group.brand || '-'}</td>
                        <td className="px-3 py-2">
                          {r.group.isSerial ? (
                            <span className="text-xs">{r.availableCount} de {r.totalCount} seriales disponibles</span>
                          ) : (
                            <input
                              type="number"
                              min={0}
                              max={r.availableCount}
                              value={qtyOverrides[r.key] ?? r.availableCount}
                              disabled={disabled}
                              onChange={(e) => setQtyOverrides((prev) => ({ ...prev, [r.key]: Math.max(0, Math.min(r.availableCount, parseInt(e.target.value) || 0)) }))}
                              className="w-20 border rounded px-2 py-1"
                            />
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {disabled ? <span className="text-red-600 text-xs">No disponible</span> : r.availableCount}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Destinatario</label>
              <select
                value={assigneeType}
                onChange={(e) => { setAssigneeType(e.target.value); setResponsibleDoc(''); setVehicleId(''); }}
                className="w-full border rounded px-3 py-2"
              >
                <option value="person">Persona</option>
                <option value="vehicle">Vehículo</option>
              </select>
            </div>
            {assigneeType === 'vehicle' ? (
              <div>
                <label className="block text-sm font-medium mb-1">Vehículo (api.vehicles)</label>
                <select
                  value={vehicleId}
                  onChange={(e) => setVehicleId(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Seleccione placa...</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>{v.plate}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Dotación: {VEHICLE_DOTACION_CATEGORIES.map((c) => c.label).join(', ')}</p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-1">Persona responsable</label>
                <select
                  value={responsibleDoc}
                  onChange={(e) => setResponsibleDoc(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Seleccione...</option>
                  {(people || [])
                    .filter((p) => String(p?.status || '').trim().toLowerCase() === 'activo')
                    .map((p) => (
                      <option key={p.id} value={p.dni || p.id}>{p.name} - {p.dni || p.id}</option>
                    ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Observaciones (opcional)</label>
              <input
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                className="w-full border rounded px-3 py-2"
              />
            </div>
          </div>

          {error ? <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div> : null}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded border">Cancelar</button>
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
            >
              {submitting ? 'Asignando...' : 'Asignar cargue'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
